import {
  PAGE_SIZE_OPTIONS,
  type PageSize,
  type PageSizeOptions,
  type PaginationMeta,
} from "@design-system/pagination";
import type { DataAttributes } from "@design-system/utils";
import {
  useTable,
  type ColumnOrderState,
  type ColumnVisibilityState,
  type OnChangeFn,
  type RowData,
  type RowSelectionState,
  type SortingState,
} from "@tanstack/react-table";
import React, { useCallback, useMemo } from "react";

import { DataTableContext, type DataTableContextValue } from "../context";
import {
  dataTableFeatures,
  type DataTableColumnDef,
  type DataTableFeatures,
} from "../features";
import type { SortOrder } from "../schemas";
import { toReactTablePagination } from "../utils";

interface DataTableSearch<
  TSortBy = string,
  TPageSize extends PageSizeOptions = typeof PAGE_SIZE_OPTIONS,
> {
  sortBy?: TSortBy;
  sortOrder?: SortOrder;
  page: number;
  pageSize: PageSize<TPageSize>;
}

interface DataTableSearchInput<TSortBy = string> {
  sortBy?: TSortBy;
  sortOrder?: SortOrder;
  page: number;
  pageSize: number;
}

interface SharedProviderProps<
  TData extends RowData,
  TSortBy = string,
  TPageSize extends PageSizeOptions = typeof PAGE_SIZE_OPTIONS,
> {
  data: TData[];
  columns: DataTableColumnDef<TData>[];
  search: DataTableSearchInput<TSortBy>;
  meta?: PaginationMeta;
  onSearchChange: (
    updates: Partial<DataTableSearch<TSortBy, TPageSize>>
  ) => void;
  onRowClick?: (item: TData) => void;
  getRowAttributes?: (item: TData) => DataAttributes;
  onRowFocus?: (item: TData | null) => void;
  onEscape?: () => void;
  rowSelection?: RowSelectionState;
  columnVisibility?: ColumnVisibilityState;
  onColumnVisibilityChange?: OnChangeFn<ColumnVisibilityState>;
  /**
   * Left empty, TanStack reads no explicit order and the columns appear as
   * declared, so a table only names an order once its reader has changed one.
   */
  columnOrder?: ColumnOrderState;
  onColumnOrderChange?: OnChangeFn<ColumnOrderState>;
  getRowId?: (row: TData, index: number) => string;
  className?: string;
  children: React.ReactNode;
}

type SortingModeProps =
  | { manualSorting?: true; allowUnsorted?: boolean }
  | { manualSorting: false; allowUnsorted?: never };

type ProviderProps<
  TData extends RowData,
  TSortBy = string,
  TPageSize extends PageSizeOptions = typeof PAGE_SIZE_OPTIONS,
> = SharedProviderProps<TData, TSortBy, TPageSize> & SortingModeProps;

function Provider<
  TData extends RowData,
  TSortBy = string,
  TPageSize extends PageSizeOptions = typeof PAGE_SIZE_OPTIONS,
>({
  data,
  columns,
  search,
  meta,
  onSearchChange,
  manualSorting = true,
  allowUnsorted = false,
  onRowClick,
  getRowAttributes,
  onRowFocus,
  onEscape,
  rowSelection,
  columnVisibility,
  onColumnVisibilityChange,
  columnOrder,
  onColumnOrderChange,
  getRowId,
  children,
}: Readonly<ProviderProps<TData, TSortBy, TPageSize>>) {
  const handleSort = useCallback(
    (sortBy: TSortBy) => {
      const isSortedDescending =
        search.sortBy === sortBy && search.sortOrder === "desc";

      if (allowUnsorted && isSortedDescending) {
        onSearchChange({ sortBy: undefined, sortOrder: undefined, page: 1 });
        return;
      }

      const newSortOrder: SortOrder =
        search.sortBy === sortBy && search.sortOrder === "asc" ? "desc" : "asc";

      onSearchChange({
        sortBy,
        sortOrder: newSortOrder,
        page: 1,
      });
    },
    [allowUnsorted, search.sortBy, search.sortOrder, onSearchChange]
  );

  const handlePageChange = useCallback(
    (pageIndex: number) => {
      onSearchChange({ page: pageIndex + 1 });
    },
    [onSearchChange]
  );

  const handlePageSizeChange = useCallback(
    (pageSize: number) => {
      onSearchChange({ pageSize: pageSize, page: 1 });
    },
    [onSearchChange]
  );

  const sorting = useMemo<SortingState>(
    () =>
      search.sortBy === undefined
        ? []
        : [{ id: String(search.sortBy), desc: search.sortOrder === "desc" }],
    [search.sortBy, search.sortOrder]
  );

  const table = useTable<DataTableFeatures, TData>({
    features: dataTableFeatures,
    data,
    columns,
    onPaginationChange: (updater) => {
      const reactTablePagination = toReactTablePagination({
        pageIndex: search.page - 1,
        pageSize: search.pageSize,
      });
      const newPagination =
        typeof updater === "function" ? updater(reactTablePagination) : updater;

      if (newPagination.pageSize !== reactTablePagination.pageSize) {
        handlePageSizeChange(newPagination.pageSize);
      } else if (newPagination.pageIndex !== reactTablePagination.pageIndex) {
        handlePageChange(newPagination.pageIndex);
      }
    },
    manualPagination: true,
    pageCount: meta?.totalPages || -1,
    manualSorting,
    getRowId,
    autoResetCellSelection: false,
    ...(rowSelection !== undefined && { enableRowSelection: true }),
    onColumnVisibilityChange,
    onColumnOrderChange,
    state: {
      pagination: toReactTablePagination({
        pageIndex: search.page - 1,
        pageSize: search.pageSize,
      }),
      sorting,
      ...(rowSelection !== undefined && { rowSelection }),
      // Spread only when named: `state` overrides what the table holds itself, so
      // an absent prop must stay absent rather than arrive as undefined.
      ...(columnVisibility !== undefined && { columnVisibility }),
      ...(columnOrder !== undefined && { columnOrder }),
    },
  });

  const value = useMemo(
    () => ({
      table,
      search,
      meta,
      onRowClick,
      getRowAttributes,
      onRowFocus,
      onEscape,
      handleSort,
      handlePageChange,
      handlePageSizeChange,
    }),
    [
      table,
      search,
      meta,
      onRowClick,
      getRowAttributes,
      onRowFocus,
      onEscape,
      handleSort,
      handlePageChange,
      handlePageSizeChange,
    ]
  );

  return (
    <DataTableContext.Provider
      value={value as unknown as DataTableContextValue<RowData, unknown>}
    >
      {children}
    </DataTableContext.Provider>
  );
}

export { Provider };
export type { ProviderProps, DataTableSearch };
